import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, customersApi } from '@/lib/api'
import type { McpKey, Role, Team, TeamMember, TeamSubscription } from '@/lib/types'
import { useCurrentTeam } from '@/features/team/current-team'

export function useTeam() {
  const { ready } = useCurrentTeam()
  return useQuery({ queryKey: ['team', 'settings'], enabled: ready, queryFn: async () => (await api.get<Team>('/team')).data })
}

export function useUpdateTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; date_time_format: string; currency: string }) => api.put<Team>('/team', body),
    // the current team carries name, date format and currency for every page
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })
}

export function useWipeTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/team/wipe'),
    onSuccess: () => qc.invalidateQueries(),
  })
}

export function useTeamMembers() {
  const { ready } = useCurrentTeam()
  return useQuery({ queryKey: ['team', 'members'], enabled: ready, queryFn: async () => (await api.get<TeamMember[]>('/team/users')).data ?? [] })
}

export function useInviteMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; role: Role }) => api.post('/team/users', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })
}

export function useUpdateMemberRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, role }: { id: number; role: Role }) => api.put(`/team/users/${id}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team', 'members'] }),
  })
}

export function useRemoveMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/team/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })
}

export function useSubscription() {
  const { ready } = useCurrentTeam()
  return useQuery({
    queryKey: ['team', 'subscription'],
    enabled: ready,
    queryFn: async () => (await api.get<{ subscription: TeamSubscription | null }>('/team/subscription')).data.subscription,
  })
}

export type BillingCycle = 'monthly' | 'yearly'

/** Both return a Stripe URL; the browser goes there. */
export function useCheckout() {
  return useMutation({
    mutationFn: async (cycle: BillingCycle) =>
      (await api.post<{ url: string }>('/team/subscription/create-checkout-session', { plan: `premium_${cycle}`, billing_cycle: cycle })).data.url,
    onSuccess: (url) => window.location.assign(url),
  })
}

export function useBillingPortal() {
  return useMutation({
    mutationFn: async () => (await api.post<{ url: string }>('/team/subscription/create-portal-session', {})).data.url,
    onSuccess: (url) => window.location.assign(url),
  })
}

export function useCancelSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/team/subscription/cancel'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })
}

export function useResumeSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/team/subscription/resume', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  })
}

// Custom fields live on the customers API; the list query is useCustomFields
// in features/companies. Company records carry the values, so refresh those too.
export function useSaveCustomField() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id?: number; name: string }) =>
      id ? customersApi.put(`/customer/custom-fields/${id}`, { name }) : customersApi.post('/customer/custom-fields', { name }),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['custom-fields'] }), qc.invalidateQueries({ queryKey: ['company'] })]),
  })
}

export function useDeleteCustomField() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => customersApi.delete(`/customer/custom-fields/${id}`),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['custom-fields'] }), qc.invalidateQueries({ queryKey: ['company'] })]),
  })
}

export function useMcpKeys() {
  const { ready } = useCurrentTeam()
  return useQuery({ queryKey: ['mcp-keys'], enabled: ready, queryFn: async () => (await api.get<McpKey[]>('/integrations/mcp')).data ?? [] })
}

export function useGenerateMcpKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (name: string) => (await api.post<{ id: number; name: string; api_key: string }>('/integrations/mcp/generate', { name })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp-keys'] }),
  })
}

export function useRevokeMcpKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/integrations/mcp/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp-keys'] }),
  })
}

/** GET /team/ai-settings: which model the assistant and the city's agent use. */
export type AiSettings = {
  mode: 'builtin' | 'own'
  provider: 'gemini'
  model: string
  hasKey: boolean
  keyHint?: string
  verifiedAt?: string
  verifiedModel?: string
  /** The plan allows the team's own key. */
  available: boolean
  builtinConfigured: boolean
  /** The server can store keys (AI_KEY_SECRET is set). */
  ownKeysConfigured: boolean
}

export function useAiSettings() {
  const { ready } = useCurrentTeam()
  return useQuery({ queryKey: ['team', 'ai-settings'], enabled: ready, queryFn: async () => (await api.get<AiSettings>('/team/ai-settings')).data })
}

function useAiSettingsMutation<T>(fn: (arg: T) => Promise<AiSettings>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: (data) => qc.setQueryData(['team', 'ai-settings'], data) })
}

export function useSaveAiSettings() {
  return useAiSettingsMutation(
    async (body: { mode: AiSettings['mode']; apiKey?: string; model?: string }) => (await api.put<AiSettings>('/team/ai-settings', body)).data,
  )
}

export function useTestAiKey() {
  return useAiSettingsMutation(async () => (await api.post<AiSettings>('/team/ai-settings/test')).data)
}

export function useRemoveAiKey() {
  return useAiSettingsMutation(async () => (await api.delete<AiSettings>('/team/ai-settings/key')).data)
}
